import type { Participant, Stream, VideoPlayer } from "@zoom/videosdk";
import type { RemoteVideoDelegator } from "../../../../core/room/participant/media/MediaDelegator";
import { MediaSource } from "../../../../core/room/participant/media/types";
import { handleError } from "../../../utils/handleError";

/**
 * Handles remote screen share video for Zoom participants.
 * Unlike camera video, screen share uses Zoom's startShareView/stopShareView APIs.
 */
export class ZoomRemoteScreenShareDelegator implements RemoteVideoDelegator {
  readonly kind = "video" as const;
  readonly source = MediaSource.Screen as const;
  #participant: Participant;
  #zoomSession: typeof Stream;

  constructor(participant: Participant, zoomSession: typeof Stream) {
    this.#participant = participant;
    this.#zoomSession = zoomSession;
  }

  get sid() {
    return `${this.#participant.userId}:${this.kind}:${this.source}`;
  }

  get isEnabled() {
    // always true because instance gets teared down when disabled
    return true;
  }

  /**
   * Renders screen share using Zoom's attachShareView/detachShareView APIs.
   * https://developers.zoom.us/docs/video-sdk/web/share/#share-the-active-view
   */
  async attach(element: VideoPlayer) {
    try {
      await this.#zoomSession.attachShareView(
        this.#participant.userId,
        element,
      );
    } catch (attachError) {
      handleError("[ZoomRemoteScreenShareVideoDelegator]: attach", attachError);
    }
  }

  async detach(element: VideoPlayer) {
    try {
      await this.#zoomSession.detachShareView(
        this.#participant.userId,
        element,
      );
    } catch (detachError) {
      handleError("[ZoomRemoteScreenShareVideoDelegator]: detach", detachError);
    }
  }
}
